/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import type { ContainerSetDetails, Container } from '@modules/cloud-api/v1/types'

import { FETCH_CONTAINER, FETCH_CONTAINER_SET, FETCH_CONTAINER_SETS } from '../../constants/actions'
import { replaceIn } from '../../lib/immutability-helpers'

import type { ReduxState } from '@/types/redux'

export type State = {
  containerSets: {
    [regionId: string]: ContainerSetDetails[]
  }
  containerSetById: {
    [containerSetCrumbs: string]: ContainerSetDetails
  }
  containersById: {
    [containerCrumbs: string]: Container
  }
}

const initialState: State = {
  containerSets: {},
  containerSetById: {},
  containersById: {},
}

export default function containerSetsReducer(state: State = initialState, action) {
  if (action.type === FETCH_CONTAINER_SETS) {
    if (!action.error && action.payload) {
      const { regionId } = action.meta

      return replaceIn(state, [`containerSets`, regionId], action.payload.container_sets)
    }
  }

  if (action.type === FETCH_CONTAINER_SET) {
    if (!action.error && action.payload) {
      const { regionId, containerSetId } = action.meta
      const crumbs = `${regionId}/${containerSetId}`

      return replaceIn(state, [`containerSetById`, crumbs], action.payload)
    }
  }

  if (action.type === FETCH_CONTAINER) {
    if (!action.error && action.payload) {
      const { regionId, containerSetId, containerId } = action.meta
      const crumbs = `${regionId}/${containerSetId}/${containerId}`

      return replaceIn(state, [`containersById`, crumbs], action.payload)
    }
  }

  return state
}

function _getContainerSets(state: State, regionId: string): ContainerSetDetails[] | null {
  return state.containerSets[regionId] || null
}

function _getContainerSet(
  state: State,
  regionId: string,
  containerSetId: string,
): ContainerSetDetails | null {
  const containerSetCrumbs = `${regionId}/${containerSetId}`
  return state.containerSetById[containerSetCrumbs] || null
}

function _getContainer(
  state: State,
  regionId: string,
  containerSetId: string,
  containerId: string,
): Container | null {
  const containerCrumbs = `${regionId}/${containerSetId}/${containerId}`
  return state.containersById[containerCrumbs] || null
}

export const getContainer = (
  state: ReduxState,
  regionId: string,
  containerSetId: string,
  containerId: string,
) => _getContainer(state.containerSets, regionId, containerSetId, containerId)

export const getContainerSet = (state: ReduxState, regionId: string, containerSetId: string) =>
  _getContainerSet(state.containerSets, regionId, containerSetId)

export const getContainerSets = (state: ReduxState, regionId: string) =>
  _getContainerSets(state.containerSets, regionId)
